import { useMemo, useState } from "react";
import { Button } from "../../../components/buttons/button";
import { NumberCard } from "../../../components/cards/numberCard";
import Dropdown from "../../../components/dropdown/dropdown";
import { Checkbox } from "../../../components/inputs/checkbox";
import { SearchInput } from "../../../components/inputs/searchInput";
import { TabNav } from "../../../components/tabNav";
import { TabSelect } from "../../../components/tabNav/tabSelect";
import { PageHeader } from "../../../components/typography/pageHeader";
import { BgWrapper } from "../../../components/wrapper/bgWrapper";
import { useGetAllEventsQuery } from "../../../redux/features/admin/event/events.api";
import { GetAllEventsQuery } from "../../../redux/features/admin/event/events.types";
import { useExportDataMutation } from "../../../redux/features/misc/misc.api";
import { EventTable } from "../../../components/event/eventTable";
import { EventStatus } from "../../../redux/features/event/event.types";
import { useSearchParams } from "react-router-dom";

type FilterKey = "eventType" | "ticketType";

const queryTypes: { title: string; value: string; key: FilterKey }[] = [
  { title: "Physical", value: "physical", key: "eventType" },
  { title: "Virtual", value: "virtual", key: "eventType" },
  { title: "Paid Tickets", value: "paid", key: "ticketType" },
  { title: "Free Tickets", value: "free", key: "ticketType" },
];

export const EventsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") as EventStatus ;
  const [query, setQuery] = useState<GetAllEventsQuery>({
    page: 1,
    limit: 50,
    search: undefined,
    sortOrder: "desc",
    eventType: undefined,
    ticketType: undefined,
  });

  const [pendingFilters, setPendingFilters] = useState<
    Pick<GetAllEventsQuery, "eventType" | "ticketType">
  >({
    eventType: query.eventType,
    ticketType: query.ticketType,
  });

  const tabs = [
    {
      title: "All",
      to: "all",
      onClick: () => {
        setSearchParams({});
      },
    },
    {
      title: "Upcoming",
      to: "upcoming",
      onClick: () => {
        setSearchParams({
          tab: "upcoming",
        });
      },
    },
    {
      title: "Past",
      to: "completed",
      onClick: () => {
        setSearchParams({
          tab: "completed",
        });
      },
    },
    {
      title: "Draft",
      to: "draft",
      onClick: () => {
        setSearchParams({
          tab: "draft",
        });
      },
    },
    {
      title: "Flagged",
      to: "flagged",
      onClick: () => {
        setSearchParams({
          tab: "flagged",
        });
      },
    },
    // {
    //   title: "Cancelled",
    //   to: "cancelled",
    //   onClick: () => {
    //        setSearchParams({
    //          tab: "cancelled",
    //        });
    //   },
    // },
  ];
  const queryParams = useMemo(() => {
    const status = activeTab ? activeTab : undefined;
    return {
      ...query,
      search: query.search === "" ? undefined : query.search,
      status: status,
      eventType: query.eventType?.length ? query.eventType : undefined,
      ticketType: query.ticketType?.length ? query.ticketType : undefined,
    };
    // eslint-disable-next-line
  }, [
    query.page,
    query.limit,
    query.search,
    query.sortOrder,
    query.eventType,
    query.ticketType,
    activeTab,
  ]);

  const [exportData, { isLoading: exportingData }] = useExportDataMutation();

  const {
    isFetching: fetchingEvents,
    isLoading: loadingEvents,
    data: eventsData,
  } = useGetAllEventsQuery(queryParams, {
    refetchOnMountOrArgChange: true,
  });

  return (
    <div className="w-full">
      <PageHeader header={"Events"} className="font-unbounded font-normal" />

      <TabNav
        navs={tabs}
        className="mt-8 !min-w-full md:!min-w-fit md:!max-w-fit"
        tabClassName=""
        activeTab={activeTab ?? "all"}
        variant="switcher"
        theme="plain"
      />

      <div className="mt-3 flex flex-col gap-[2px]">
        <BgWrapper className="grid grid-cols-1 gap-[10px] rounded-b-none md:grid-cols-3">
          <NumberCard
            value={eventsData?.data?.stats?.total ?? 0}
            text="Total Events"
            isCurrency={false}
            loading={loadingEvents}
            titleFirst
          />
          <NumberCard
            value={eventsData?.data?.stats?.upcoming ?? 0}
            text="Upcoming Events"
            isCurrency={false}
            loading={loadingEvents}
            titleFirst
          />

          <NumberCard
            value={eventsData?.data?.stats?.past ?? 0}
            text="Past Events"
            isCurrency={false}
            loading={loadingEvents}
            titleFirst
          />
        </BgWrapper>
        <BgWrapper className="rounded-t-none">
          <div className="flex w-full flex-col items-center justify-between gap-4 md:flex-row">
            <SearchInput
              searchFunction={(value) => {
                setQuery({
                  ...query,
                  search: value,
                  page: 1,
                });
              }}
              placeholder="Search"
              className="md:!max-w-[300px]"
            />

            <div className="grid w-full grid-cols-2 items-center gap-3 md:flex md:w-fit md:flex-row">
              <Dropdown
                className="order-last col-span-full md:order-first"
                position="bottom start"
                closeOnClick={false}
                onOpen={() => {
                  setPendingFilters({
                    eventType: query.eventType,
                    ticketType: query.ticketType,
                  });
                }}
                customMenuItems={({ close }) => {
                  return (
                    <div className="flex flex-col rounded-xl border border-grey-200 bg-white">
                      {queryTypes.map((f) => {
                        const selectedValues = (pendingFilters[f.key] ??
                          []) as string[];
                        const isChecked = selectedValues.includes(f.value);

                        return (
                          <Checkbox
                            key={f.value}
                            checkStatus={isChecked}
                            text={f.title}
                            handleChange={(checked) => {
                              const updatedValues = checked
                                ? [...selectedValues, f.value]
                                : selectedValues.filter((v) => v !== f.value);

                              setPendingFilters({
                                ...pendingFilters,
                                [f.key]:
                                  updatedValues.length > 0
                                    ? updatedValues
                                    : undefined,
                              });
                            }}
                            className="w-full border-b p-3"
                          />
                        );
                      })}

                      <div className="w-full p-2">
                        <Button
                          type="button"
                          className="w-full"
                          variation="primary"
                          text="Apply"
                          onClick={() => {
                            setQuery({
                              ...query,
                              ...pendingFilters,
                              page: 1,
                            });
                            close();
                          }}
                        />
                      </div>
                    </div>
                  );
                }}
              >
                <Button
                  type="button"
                  text="Filter"
                  iconDimension={20}
                  className="!min-w-fit !rounded-2xl !p-3 !text-sm"
                  icon="filter"
                  variation="grey"
                />
              </Dropdown>

              <Button
                type="button"
                onClick={() => {
                  if (query.sortOrder === "asc") {
                    setQuery({ ...query, sortOrder: "desc" });
                  } else {
                    setQuery({ ...query, sortOrder: "asc" });
                  }
                }}
                text="Date Created"
                className="!min-w-fit !rounded-2xl !p-3 !text-sm"
                icon={query.sortOrder === "asc" ? "order-asc" : "order-desc"}
                variation="grey"
              />

              <Button
                type="button"
                onClick={async () => {
                  await exportData({
                    type: "events",
                    fetchAll: true,
                    sortOrder: query.sortOrder,
                    search: query.search || undefined,
                    searchTerm: query.search || undefined,
                    status: query.status,
                  });
                }}
                text="Export"
                className="!min-w-fit !rounded-2xl !p-3 !text-sm"
                icon="export"
                variation="grey"
                loading={exportingData}
              />
            </div>
          </div>

          {((query.eventType && query.eventType?.length > 0) ||
            (query.ticketType && query.ticketType?.length > 0)) && (
            <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
              <p className="text-base font-bold text-grey-600">
                {eventsData?.data?.totalCount} Event
                {(eventsData?.data?.totalCount ?? 0) > 1 && "s"}
              </p>
              <div className="mt-3 flex flex-wrap items-center justify-end">
                <TabSelect
                  tabClassName="capitalize"
                  tabs={[
                    ...(query.eventType?.map((v) => ({
                      title: v,
                      to: v,
                      onClick: () => {},
                      onRemove: () => {
                        const updated =
                          query.eventType?.filter((ev) => ev !== v) ||
                          undefined;
                        setQuery({ ...query, eventType: updated });
                        setPendingFilters({
                          ...pendingFilters,
                          eventType: updated,
                        });
                      },
                    })) ?? []),
                    ...(query.ticketType?.map((v) => ({
                      title: v,
                      to: v,
                      onClick: () => {},
                      onRemove: () => {
                        const updated =
                          query.ticketType?.filter((tk) => tk !== v) ||
                          undefined;
                        setQuery({ ...query, ticketType: updated });
                        setPendingFilters({
                          ...pendingFilters,
                          ticketType: updated,
                        });
                      },
                    })) ?? []),
                  ]}
                />
              </div>
            </div>
          )}
          <EventTable
            eventsData={eventsData}
            query={query}
            fetchingEvents={fetchingEvents}
            setQuery={setQuery}
          />
        </BgWrapper>
      </div>
    </div>
  );
};
